import * as THREE from 'three';
import { leafDimensions } from './leafDimensions.js';
import { closedProfileDistances } from './woodUV.js';

// User's cassette drawing: X runs from the outer lip toward the glass;
// Y runs from the glass seat toward the exterior. The leaf surface is Y=26.
export const CASSETTE = {
  widthMm: 32, topWidthMm: 24, overlapMm: 10, depthMm: 26,
  projectionMm: 5, radiusMm: 4, grooveWidthMm: 5,
  grooveDepthMm: 8, grooveRightInsetMm: 12
};

// Sample exact circular fillets at the two upper corners of the drawing.
function fillet(a, b, c, radius, segments = 8) {
  const va = new THREE.Vector2(a[0] - b[0], a[1] - b[1]).normalize();
  const vc = new THREE.Vector2(c[0] - b[0], c[1] - b[1]).normalize();
  const angle = Math.acos(THREE.MathUtils.clamp(va.dot(vc), -1, 1));
  const tangent = radius / Math.tan(angle / 2);
  const center = va.clone().add(vc).normalize().multiplyScalar(radius / Math.sin(angle / 2))
    .add(new THREE.Vector2(...b));
  const first = va.clone().multiplyScalar(tangent).add(new THREE.Vector2(...b)).sub(center);
  const last = vc.clone().multiplyScalar(tangent).add(new THREE.Vector2(...b)).sub(center);
  const start = Math.atan2(first.y, first.x);
  let sweep = Math.atan2(last.y, last.x) - start;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  while (sweep < -Math.PI) sweep += 2 * Math.PI;
  return Array.from({ length: segments + 1 }, (_, i) => [
    center.x + radius * Math.cos(start + sweep * i / segments),
    center.y + radius * Math.sin(start + sweep * i / segments)
  ]);
}

export function cassetteProfileMm(spec = CASSETTE, leaf = {}) {
  const { thicknessMm } = leafDimensions(leaf);
  const { widthMm: w, topWidthMm: t, overlapMm: o, depthMm: d,
    projectionMm: p, radiusMm: r, grooveWidthMm: gw,
    grooveDepthMm: gd, grooveRightInsetMm: gr } = spec;
  if (!Object.values(spec).every(Number.isFinite) ||
      !(w > t && t > o && o > 0 && d > 0 && d < thicknessMm / 2 &&
        p > 0 && r > 0 && r <= p && r * 2 < t &&
        gw > 0 && gd > 0 && gd < d && gr > 0 && w - gr - gw > o)) {
    throw new Error('Invalid cassette dimensions: check the lip, depth, R4 corners and groove.');
  }
  return [
    [0, d],
    ...fillet([0, d], [0, d + p], [t, d + p], r),
    ...fillet([0, d + p], [t, d + p], [w, 0], r),
    [w, 0], [w - gr, 0], [w - gr, gd],
    [w - gr - gw, gd], [w - gr - gw, 0], [o, 0], [o, d]
  ];
}

/** One closed, mitred ring on each face. Returned geometries belong to caller. */
export function createWindowCassetteGeometries(opening, spec = CASSETTE, leaf = {}) {
  const { frontMm, backMm } = leafDimensions(leaf);
  const profile = cassetteProfileMm(spec, leaf);
  const profileDistances = closedProfileDistances(profile);
  const inset = spec.widthMm - spec.overlapMm;
  if (Math.min(opening.widthMm, opening.heightMm) <= 2 * inset + 1) {
    throw new Error('Window is too small for the cassette profile.');
  }
  return ['front', 'back'].map((side) => {
    const positions = [];
    profile.forEach(([x, y]) => {
      const r = x - spec.overlapMm;
      const left = opening.xMm + r;
      const right = opening.xMm + opening.widthMm - r;
      const bottom = opening.yMm + r;
      const top = opening.yMm + opening.heightMm - r;
      const z = side === 'front' ? frontMm + y - spec.depthMm
        : backMm - y + spec.depthMm;
      [[left, bottom], [right, bottom], [right, top], [left, top]].forEach(([px, py]) => {
        positions.push(px / 1000, py / 1000, z / 1000);
      });
    });
    const indices = [];
    profile.forEach((_, i) => {
      for (let corner = 0; corner < 4; corner++) {
        const a = i * 4 + corner;
        const b = i * 4 + (corner + 1) % 4;
        const c = ((i + 1) % profile.length) * 4 + (corner + 1) % 4;
        const d = ((i + 1) % profile.length) * 4 + corner;
        if (side === 'front') indices.push(a, b, c, a, c, d);
        else indices.push(a, c, b, a, d, c);
      }
    });
    const indexed = new THREE.BufferGeometry();
    indexed.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    indexed.setIndex(indices);
    const geometry = indexed.toNonIndexed();
    indexed.dispose();
    geometry.computeVertexNormals();
    const p = geometry.getAttribute('position');
    const uv = new Float32Array(p.count * 2);
    // Each mitred rail is a separate UV island. Non-indexed vertices let the
    // grain turn 90° at a corner while the ring remains one watertight solid.
    profile.forEach((_, section) => {
      const nextProfile = side === 'front' ? [0,0,1,0,1,1] : [0,1,0,0,1,1];
      for (let rail = 0; rail < 4; rail++) for (let vertex = 0; vertex < 6; vertex++) {
        const i = (section * 4 + rail) * 6 + vertex;
        uv[i * 2] = profileDistances[section + nextProfile[vertex]] / 1000;
        uv[i * 2 + 1] = rail % 2 === 0 ? p.getX(i) - opening.xMm / 1000 : p.getY(i) - opening.yMm / 1000;
      }
    });
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    return geometry;
  });
}
